import {
  CURIOSITY_CARD_VARIANTS,
  DEFAULT_REFINE_CARD_VARIANT_ID,
  getResultCardVariants,
} from "./refineCardVariants";
import {
  getCardLanguagePresentation,
  normalizeLanguageMetadata,
  normalizeTranslationLanguages,
} from "../../utils/cards/languageAdaptiveCard";

const FALLBACK_RESULT_MESSAGE =
  "I’ve been thinking about how to care for you better. Is there something you wish you could tell me?";

function getTrimmedString(value) {
  return typeof value === "string" ? value.trim() : "";
}

function getPersistedCardQuestion(card) {
  return (
    getTrimmedString(card?.questionRefined) ||
    getTrimmedString(card?.question) ||
    getTrimmedString(card?.refinedQuestion)
  );
}

const LEGACY_CURIOSITY_VARIANT_IDS = {
  values: "see",
  emotions: "explore",
  logistics: "express",
};

export function getCardTranslationStatus(card) {
  return card?.translationStatus === "needs_refresh"
    ? "needs_refresh"
    : "current";
}

export function validateCardQuestion(value, savedValue = "") {
  const trimmedValue = getTrimmedString(value);
  const savedText = getTrimmedString(savedValue);
  const questionMarks = [...trimmedValue].filter((character) =>
    ["?", "؟", "？"].includes(character)
  );

  if (!trimmedValue) {
    return { valid: false, message: "Enter a question." };
  }

  if ([...trimmedValue].length > 280) {
    return {
      valid: false,
      message: "Keep your question to 280 characters or fewer.",
    };
  }

  if (questionMarks.length === 0) {
    return { valid: false, message: "End with a question mark." };
  }

  const trimmedCharacters = [...trimmedValue];
  const lastCharacter = trimmedCharacters[trimmedCharacters.length - 1];

  if (
    !["?", "؟", "？"].includes(lastCharacter)
  ) {
    return { valid: false, message: "End your question with a question mark." };
  }

  return {
    valid: trimmedValue !== savedText,
    message: trimmedValue === savedText ? "Make a change before saving." : "",
    value: trimmedValue,
  };
}

function getPersistedTranslationLanguage(generation, cards) {
  const generationLanguage = normalizeTranslationLanguages(
    generation?.translationLanguages
  )[0];

  const cardWithTranslation = cards.find((card) => card.translation);

  if (cardWithTranslation?.translationLanguage) {
    return getTrimmedString(cardWithTranslation.translationLanguage);
  }

  if (cardWithTranslation?.translation) {
    return getTrimmedString(generationLanguage?.language);
  }

  // Historical generations did not persist primary-language metadata. Keep
  // their requested target for auth continuation, while newer records derive
  // the effective target from actual distinct translation text above.
  return generation?.primaryLanguage || generation?.primaryLanguageCode
    ? ""
    : getTrimmedString(generationLanguage?.language);
}

export function normalizePersistedGenerationResult(
  persistedGeneration,
  selectedTranslationLanguage = "",
  immediateResult = null
) {
  const generation = persistedGeneration?.generation;
  const cards = Array.isArray(persistedGeneration?.cards)
    ? persistedGeneration.cards
    : [];
  const cardVariants = getResultCardVariants(generation);
  const isCuriosityGeneration = cardVariants === CURIOSITY_CARD_VARIANTS;
  const knownVariantIds = new Set(cardVariants.map((variant) => variant.id));
  const cardsByType = {};
  const generatedCardIdsByVariant = {};
  const variantQuestions = {};
  const normalizedCards = [];
  const primaryLanguage = normalizeLanguageMetadata({
    language: generation?.primaryLanguage || immediateResult?.primaryLanguage,
    languageCode:
      generation?.primaryLanguageCode || immediateResult?.primaryLanguageCode,
  });
  const normalizedTranslationLanguages = normalizeTranslationLanguages(
    generation?.translationLanguages?.length
      ? generation.translationLanguages
      : immediateResult?.translationLanguages ||
          (immediateResult?.translationLanguage
            ? [immediateResult.translationLanguage]
            : [])
  );

  cards.forEach((card) => {
    const persistedVariantId = getTrimmedString(card?.variantId);
    const variantId = isCuriosityGeneration
      ? LEGACY_CURIOSITY_VARIANT_IDS[persistedVariantId] || persistedVariantId
      : persistedVariantId;
    const question = getPersistedCardQuestion(card);

    if (!card?.id || !knownVariantIds.has(variantId) || !question) {
      return;
    }

    const variant = cardVariants.find((item) => item.id === variantId);
    const presentation = getCardLanguagePresentation(card, {
      generation: {
        ...generation,
        primaryLanguage: primaryLanguage.language,
        primaryLanguageCode: primaryLanguage.languageCode,
      },
      translationLanguage: normalizedTranslationLanguages[0]?.language ||
        selectedTranslationLanguage,
    });
    const normalizedCard = {
      ...card,
      type: variantId,
      variantId,
      variantLabel: variant?.label,
      variantOrder: variant?.order,
      cardId: card.id,
      question,
      primaryLanguage: presentation.primaryLanguage,
      primaryLanguageCode: presentation.primaryLanguageCode,
      translations: presentation.translations,
      translation: presentation.translation,
      translationLanguage: presentation.translationLanguage,
      translationLanguageCode: presentation.translationLanguageCode,
      translationStatus: getCardTranslationStatus(card),
    };

    if (!cardsByType[variantId]) {
      cardsByType[variantId] = normalizedCard;
      generatedCardIdsByVariant[variantId] = card.id;
      variantQuestions[variantId] = question;
      normalizedCards.push(normalizedCard);
    }
  });

  const refinedQuestion =
    variantQuestions[cardVariants[0]?.id || DEFAULT_REFINE_CARD_VARIANT_ID] ||
    normalizedCards[0]?.question ||
    "";
  const hasAllVariantCards = cardVariants.every(
    (variant) => cardsByType[variant.id]
  );

  if (!refinedQuestion || !hasAllVariantCards) {
    return null;
  }

  return {
    rawThought: generation?.rawThought || "",
    refinedQuestion,
    generation: {
      ...generation,
      primaryLanguage: primaryLanguage.language,
      primaryLanguageCode: primaryLanguage.languageCode,
      translationLanguages: normalizedTranslationLanguages,
    },
    generationId: generation?.id || normalizedCards[0]?.generationId || "",
    partnerId: generation?.partnerId || normalizedCards[0]?.partnerId || "",
    wallId: generation?.wallId || "",
    cardIds: Array.isArray(generation?.cardIds)
      ? generation.cardIds
      : normalizedCards.map((card) => card.cardId),
    cardVariants,
    generatedCardIdsByVariant,
    variantQuestions,
    cards: normalizedCards,
    cardsByType,
    message: FALLBACK_RESULT_MESSAGE,
    primaryLanguage: primaryLanguage.language,
    primaryLanguageCode: primaryLanguage.languageCode,
    translationLanguage: getPersistedTranslationLanguage(
      {
        ...generation,
        ...(generation?.primaryLanguage || generation?.primaryLanguageCode
          ? {}
          : immediateResult?.primaryLanguage || immediateResult?.primaryLanguageCode
            ? {
                primaryLanguage: immediateResult.primaryLanguage,
                primaryLanguageCode: immediateResult.primaryLanguageCode,
              }
            : {}),
      },
      normalizedCards
    ),
    translation: "",
  };
}

export function reconcileEditedCardResult(
  result,
  { cardId, questionRefined, translations, translationStatus, manualEditsCount }
) {
  if (!result || !cardId || typeof questionRefined !== "string") {
    return result;
  }

  const variantEntry = Object.entries(result.generatedCardIdsByVariant || {}).find(
    ([, generatedCardId]) => generatedCardId === cardId
  );

  if (!variantEntry) {
    return result;
  }

  const [variantId] = variantEntry;
  const existingCard = result.cardsByType?.[variantId];

  if (!existingCard) {
    return result;
  }

  const nextCard = {
    ...existingCard,
    questionRefined: questionRefined.trim(),
    question: questionRefined.trim(),
    translations: Array.isArray(translations) ? translations : [],
    translationStatus: getCardTranslationStatus({ translationStatus }),
    translation: "",
    translationLanguage: "",
    translationLanguageCode: "",
    manualEditsCount,
  };
  const presentation = getCardLanguagePresentation(nextCard, {
    generation: result.generation,
    translationLanguage: result.translationLanguage,
  });
  const normalizedCard = {
    ...nextCard,
    type: variantId,
    variantId,
    cardId,
    primaryLanguage: presentation.primaryLanguage,
    primaryLanguageCode: presentation.primaryLanguageCode,
    translations: presentation.translations,
    translation: presentation.translation,
    translationLanguage: presentation.translationLanguage,
    translationLanguageCode: presentation.translationLanguageCode,
  };
  const nextVariantQuestions = {
    ...result.variantQuestions,
    [variantId]: normalizedCard.question,
  };
  const nextCardsByType = {
    ...result.cardsByType,
    [variantId]: normalizedCard,
  };

  return {
    ...result,
    refinedQuestion:
      variantId ===
      (result.cardVariants?.[0]?.id || DEFAULT_REFINE_CARD_VARIANT_ID)
        ? normalizedCard.question
        : result.refinedQuestion,
    variantQuestions: nextVariantQuestions,
    cardsByType: nextCardsByType,
    cards: (result.cards || []).map((card) =>
      card.cardId === cardId || card.id === cardId ? normalizedCard : card
    ),
  };
}

export function normalizeImmediateGenerationResult(
  immediateResult,
  selectedTranslationLanguage = ""
) {
  const cards = Array.isArray(immediateResult?.cards)
    ? immediateResult.cards
    : [];
  const generation = {
    id: immediateResult?.generationId || "",
    primaryLanguage: immediateResult?.primaryLanguage,
    primaryLanguageCode: immediateResult?.primaryLanguageCode,
    translationLanguages: immediateResult?.translationLanguage
      ? [{ language: immediateResult.translationLanguage }]
      : [],
  };
  const normalizedCards = cards.map((card) => {
    const presentation = getCardLanguagePresentation(card, {
      generation,
      translationLanguage:
        immediateResult?.translationLanguage || selectedTranslationLanguage,
    });

    return {
      ...card,
      type: card?.type || card?.variantId || "",
      question: presentation.question,
      primaryLanguage: presentation.primaryLanguage,
      primaryLanguageCode: presentation.primaryLanguageCode,
      translations: presentation.translations,
      translation: presentation.translation,
      translationLanguage: presentation.translationLanguage,
      translationLanguageCode: presentation.translationLanguageCode,
    };
  });

  return {
    ...immediateResult,
    cards: normalizedCards,
    primaryLanguage: normalizeLanguageMetadata({
      language: immediateResult?.primaryLanguage,
      languageCode: immediateResult?.primaryLanguageCode,
    }).language,
    primaryLanguageCode: normalizeLanguageMetadata({
      language: immediateResult?.primaryLanguage,
      languageCode: immediateResult?.primaryLanguageCode,
    }).languageCode,
    translationLanguages: normalizeTranslationLanguages(
      immediateResult?.translationLanguages ||
        (immediateResult?.translationLanguage
          ? [immediateResult.translationLanguage]
          : [])
    ),
  };
}
